<template>
  <tux-verification
    ref="verificationForm"
    :key="verificationFormKey"
    :channel="otpChannel"
    :sent-to="formattedMobileNumber"
    :manual-error-token.sync="otpErrorToken"
    :page-notification="generalErrorToken"
    :heading="$t('verification.headings.mobileVerify')"
    auto-submit
    show-cancel
    @submit="submit"
    @resend="resend"
    @cancel="cancel" />
</template>

<script>
import nudetect from '@mc-alberta/nudetect'
import { CustomLogger } from '@mc-alberta/supernova-scripts'
import { TuxVerification } from '@mc-alberta/tuxedo'
import { mapActions, mapGetters, mapState } from 'vuex'
import { LOADERS, NETWORK, ROUTES } from '@/constants'

export default {
  name: ROUTES.VERIFY_MOBILE,
  components: {
    TuxVerification
  },
  data() {
    return {
      otpErrorToken: '',
      generalErrorToken: '',
      // assign a key to the form component to force re-render on key change
      verificationFormKey: 0
    }
  },
  computed: {
    ...mapGetters(['isLoading', 'serviceErrorResponse', 'formattedUserMobileNumber']),
    ...mapState({
      contactInfo: (state) => state.identity.verificationData.contactInfo,
      otpChannel: (state) => state.identity.verificationChannelUsed
    }),
    formattedMobileNumber() {
      return this.formattedUserMobileNumber(this, this.contactInfo)
    }
  },
  created: function () {
    // Route to SETTINGS if the user has landed here without verificationData ( or reloading ) ~Inkh
    if (!this.contactInfo || !this.contactInfo.mobileNumber) {
      this.$router.replace({ name: ROUTES.SETTINGS })
    }
  },
  mounted() {
    this.$nextTick(() => nudetect.beginBehaviouralMonitoring())
    this.$root.$on('processingEnd', this.remountForm)
  },
  beforeDestroy() {
    this.clearServiceLayerError()
    this.$root.$off('processingEnd', this.remountForm)
  },
  methods: {
    ...mapState({
      serviceLayerError: (state) => state.serviceLayerError
    }),
    ...mapActions([
      'clearServiceLayerError',
      'setMessagePageContent',
      'startLoader',
      'stopLoader',
      'stopLoaderWithSuccess',
      'updateChannel',
      'verifyChannel'
    ]),

    async submit(otp) {
      if (this.isLoading) {
        // submit already inflight, log a 2nd attempt
        CustomLogger.error({
          event: 'MULTI_OTP_SUBMIT_ATTEMPT',
          error: new Error('Attempting to submit OTP multiple times.'),
          details: {
            routeName: this.$route.name,
            routePath: this.$route.path
          }
        })
        return
      }

      const phone = this.contactInfo
      this.$analytics.track({
        event: this.$EVENTS.CONTINUE_BUTTON,
        meta: {
          network: NETWORK.MASTERCARD,
          otpChannel: this.otpChannel.toLowerCase()
        }
      })
      this.startLoader(LOADERS.UPDATE_PROFILE)
      this.clearServiceLayerError()
      this.clearErrors()

      await this.updateChannel({ otp, phone })
      nudetect.clearBehaviouralData()
      if (this.serviceErrorResponse()) {
        this.handleError()
        return
      }

      this.$router.replace({ name: ROUTES.SETTINGS })
      this.stopLoaderWithSuccess(LOADERS.UPDATE_PROFILE)
    },

    async resend() {
      this.startLoader(LOADERS.RESEND_CODE)

      this.clearServiceLayerError()
      this.clearErrors()

      await this.verifyChannel({ phone: this.contactInfo })

      const errorResponse = this.serviceErrorResponse()
      this.generalErrorToken = errorResponse ? 'error.default' : ''

      if (this.serviceLayerError) {
        this.stopLoader(LOADERS.RESEND_CODE)
      } else {
        this.stopLoaderWithSuccess(LOADERS.RESEND_CODE)
      }
    },

    remountForm() {
      this.verificationFormKey++
    },

    handleError() {
      const { status, data } = this.serviceErrorResponse()

      if (status === 400) {
        nudetect.beginBehaviouralMonitoring()
        this.otpErrorToken = 'verification.error'
      } else if (status === 403 && data.reason === 'PERMISSION_DENIED') {
        this.$router.replace({
          name: ROUTES.CONTACT_INFORMATION_SETTINGS,
          params: {
            manualError: 'contactInformation.duplicateNumberError'
          }
        })
      } else {
        this.setMessagePageContent({
          buttonText: '',
          secondaryButtonTarget: ROUTES.CONTACT_INFORMATION_SETTINGS,
          secondaryButtonText: 'button.goBack'
        })
        this.$router.replace({ name: ROUTES.ERROR })
      }

      this.stopLoader(LOADERS.UPDATE_PROFILE)

      return !!this.otpErrorToken
    },

    clearErrors() {
      this.generalErrorToken = ''
    },

    cancel() {
      this.$router.replace({ name: ROUTES.SETTINGS })
    }
  }
}
</script>
